import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";

import { Row, Column, ProgressButton, OrganizationLogo } from "../../widgets";
import { Autocomplete } from "@react-google-maps/api";
import { extractAddress } from "../../util/autocomplete-utils";
import resolveConfig from "../../util/resolve-config";

import {
  getOrganization,
  putOrganization,
  uploadOrganizationLogo,
  deleteOrganizationLogo,
  setOrganizationTrial,
} from "../../data/organization-store";
import { useGoogleMaps } from "../../hooks/use-google-maps";
import { useAuth } from "../../hooks/use-auth";

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  logoSection: {
    marginTop: theme.spacing(1),
  },
  logoPreview: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 140,
    height: 90,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    boxSizing: "border-box",
  },
  destructiveButton: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
  trialSection: {
    marginTop: theme.spacing(2),
  },
  trialDateField: {
    width: 190,
  },
}));

function getValidationErrors(formState) {
  let errors = {};

  if (!formState.name) {
    errors.name = "Name is required";
  }

  if (!formState.seats || formState.seats < 1) {
    errors.seats = "Seats must be at least 1";
  }

  return errors;
}

// Formats a stored trial expiration for a `type="date"` input. Built from local
// date parts rather than toISOString(), which would roll the stored end-of-day
// timestamp forward to the next day for anyone west of UTC.
function toDateInputValue(trialEndsAt) {
  if (!trialEndsAt || typeof trialEndsAt.toDate !== "function") {
    return "";
  }

  const end = trialEndsAt.toDate();
  const month = `${end.getMonth() + 1}`.padStart(2, "0");
  const day = `${end.getDate()}`.padStart(2, "0");

  return `${end.getFullYear()}-${month}-${day}`;
}

// Describes a free-trial state: no trial, expired, or the expiration date with
// the number of days remaining.
function describeTrial(trialEndsAt) {
  if (!trialEndsAt || typeof trialEndsAt.toDate !== "function") {
    return "No free trial";
  }

  const end = trialEndsAt.toDate();
  const now = new Date();
  const dateLabel = end.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  if (end <= now) {
    return `Expired ${dateLabel}`;
  }

  const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  return `Expires ${dateLabel} — ${daysLeft} day${
    daysLeft === 1 ? "" : "s"
  } left`;
}

function mapFormStateToOrganization(state, organization) {
  const orgCopy = organization ? { ...organization } : {};

  orgCopy.name = state.name ?? "";
  orgCopy.address = {
    street: state.street ?? "",
    aptUnit: state.aptUnit ?? "",
    city: state.city ?? "",
    stateProvince: state.stateProvince ?? "",
    postalCode: state.postalCode ?? "",
    countryRegion: state.countryRegion ?? "",
  };
  orgCopy.subscription = { seats: state.seats ?? 1 };
  orgCopy.phone = state.phone ?? "";
  orgCopy.url = state.url ?? "";

  // `disabled` is owned by the disableOrganization/enableOrganization callables
  // and has no control on this form, so an edit must never write it — the
  // spread above preserves whatever the organization already has.

  // Initialize subscription if it doesn't exist
  if (!orgCopy.subscription) {
    orgCopy.subscription = {};
  }
  orgCopy.subscription.seats = parseInt(state.seats) || 1;
  
  // Set default product for new organizations
  if (!orgCopy.subscription.product) {
    const config = resolveConfig();
    orgCopy.subscription.product = config.subscriptionTiers.team.priceId;
  }

  if (!organization) {
    // New organization: record report defaults and, if an expiration date was
    // chosen, the free-trial period. An empty date means no trial and no alert.
    // `disabled` must be written explicitly here because the organization list
    // queries on `where("disabled", "==", false)`, which skips docs missing it.
    orgCopy.disabled = false;
    orgCopy.createdAt = firebase.firestore.FieldValue.serverTimestamp();
    orgCopy.reportSettings = {
      decimalPlaces: 2,
      supportedFormats: ["pdf", "xlsx"],
    };
    if (state.trialEndsAt) {
      // Trial is valid through the end of the selected day.
      const trialEnd = new Date(`${state.trialEndsAt}T23:59:59`);
      orgCopy.trialEndsAt = firebase.firestore.Timestamp.fromDate(trialEnd);
      orgCopy.trialAlertSent = false;
    }
  }
  // On edit the trial fields are left untouched here — they are written through
  // the setOrganizationTrial callable, not this form's save.

  return orgCopy;
}

function mapOrganizationToFormState(organization) {
  if (!organization) return initialState;

  return {
    name: organization.name ?? "",
    street: organization.address?.street ?? "",
    aptUnit: organization.address?.aptUnit ?? "",
    city: organization.address?.city ?? "",
    stateProvince: organization.address?.stateProvince ?? "",
    postalCode: organization.address?.postalCode ?? "",
    countryRegion: organization.address?.countryRegion ?? "",
    phone: organization.phone ?? "",
    url: organization.url ?? "",
    seats: organization.subscription?.seats ?? 1,
  };
}

const initialState = {
  name: "",
  street: "",
  aptUnit: "",
  city: "",
  stateProvince: "",
  postalCode: "",
  countryRegion: "",
  timezone: "",
  phone: "",
  url: "",
  seats: 1,
  trialEndsAt: "",
};

//const timezoneOptions = [];

function EditOrganizationForm(props) {
  const { organizationId, onCancel, onSave } = props;
  const classes = useStyles();

  const { isLoaded } = useGoogleMaps();

  const [organization, setOrganization] = useState(null);
  const [state, setState] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [autocomplete, setAutocomplete] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const [logoError, setLogoError] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const [trialDate, setTrialDate] = useState("");
  const [savingTrial, setSavingTrial] = useState(false);
  const [trialError, setTrialError] = useState(null);
  const logoInputRef = React.useRef(null);

  const [auth] = useAuth();
  const isSuperAdmin = auth.user?.claims?.isSuperAdmin ?? false;

  useEffect(() => {
    const loadOrganization = async () => {
      if (organizationId) {
        try {
          const org = await getOrganization(organizationId);

          setOrganization(org);
          setState(mapOrganizationToFormState(org));
          setTrialDate(toDateInputValue(org.trialEndsAt));
        } catch (error) {
          onSave({
            open: true,
            severity: "error",
            message: error.message,
            autoHideDuration: 6000,
          });
        }
      }
    };

    loadOrganization();
  }, [organizationId]);

  const getTitle = () => {
    if (organizationId) {
      return "Edit Organization";
    } else {
      return "Add Organization";
    }
  };

  const handleLoad = (autocomplete) => {
    setAutocomplete(autocomplete);
  };

  const handleClose = () => {
    onCancel();
  };

  const handleChange = (event) => {
    const name = event.target.id;
    const value = event.target.type === 'number' ? parseInt(event.target.value) || 0 : event.target.value;

    setState({
      ...state,
      [name]: value,
    });
  };

  const handlePlaceChanged = () => {
    if (!autocomplete) {
      console.log("Autocomplete is not loaded yet");
      return;
    }

    try {
      const place = autocomplete.getPlace();
      if (place && place.address_components) {
        const address = extractAddress(place);
        setState({...state, ...address});
      }
    } catch (error) {
      console.error("Error processing place selection:", error);
    }
  };



  const handleLogoButtonClick = () => {
    logoInputRef.current?.click();
  };

  const handleLogoSelected = async (event) => {
    const file = event.target.files?.[0];
    // Clear the value so selecting the same file again still fires onChange.
    event.target.value = "";

    if (!file) return;

    setUploadingLogo(true);
    setLogoError(null);

    try {
      await uploadOrganizationLogo(organizationId, file);
      // Remount the preview so it refetches the freshly uploaded logo.
      setLogoVersion((version) => version + 1);
    } catch (error) {
      setLogoError(error.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleClearLogo = async () => {
    setUploadingLogo(true);
    setLogoError(null);

    try {
      await deleteOrganizationLogo(organizationId);
      // Remount the preview so it reflects the now-missing logo.
      setLogoVersion((version) => version + 1);
    } catch (error) {
      setLogoError(error.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  // The trial is applied on its own rather than with the form's Save because
  // saving overwrites the whole organization document from the copy loaded when
  // this dialog opened, which would write a stale trial back over this one.
  const applyTrial = async (nextTrialDate) => {
    setSavingTrial(true);
    setTrialError(null);

    try {
      const result = await setOrganizationTrial(organizationId, nextTrialDate);

      setTrialDate(nextTrialDate);
      setOrganization((current) => ({
        ...current,
        trialEndsAt: result.trialEndsAt
          ? firebase.firestore.Timestamp.fromDate(new Date(result.trialEndsAt))
          : undefined,
      }));
    } catch (error) {
      setTrialError(error.message);
    } finally {
      setSavingTrial(false);
    }
  };

  // Guarded so an empty date can never reach applyTrial, where a null would be
  // read as a request to remove the trial.
  const handleApplyTrial = () => {
    if (!trialDate) return;
    applyTrial(trialDate);
  };

  const handleClearTrial = () => applyTrial(null);

  const handleSave = async () => {
    const validationErrors = getValidationErrors(state);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);

    try {
      const result = await putOrganization(
        mapFormStateToOrganization(state, organization),
        organizationId
      );

      onSave({
        open: true,
        message: result.message,
        severity: "success",
        autoHideDuration: 6000,
      });
    } catch (error) {
      onSave({
        open: true,
        message: error.message,
        severity: "error",
        autoHideDuration: 6000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <Backdrop className={classes.backdrop} open={saving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <DialogTitle id="form-dialog-title">{getTitle()}</DialogTitle>
      <DialogContent>
        <Column padding={2}>
          {organizationId && (
            <div className={classes.logoSection}>
              <Typography variant="subtitle2" gutterBottom>
                Report Logo
              </Typography>
              <Row spacing={2} verticalAlignment="centerVertically">
                <div className={classes.logoPreview}>
                  <OrganizationLogo
                    key={logoVersion}
                    organizationId={organizationId}
                    width={120}
                    height={72}
                    onLoadStateChange={setHasLogo}
                    placeholder={
                      <Typography variant="caption" color="textSecondary">
                        No logo uploaded
                      </Typography>
                    }
                  />
                </div>
                <ProgressButton
                  variant="outlined"
                  color="primary"
                  loading={uploadingLogo}
                  onClick={handleLogoButtonClick}
                >
                  Upload Logo
                </ProgressButton>
                {hasLogo && (
                  <Button
                    className={classes.destructiveButton}
                    variant="outlined"
                    disabled={uploadingLogo}
                    onClick={handleClearLogo}
                  >
                    Clear Logo
                  </Button>
                )}
              </Row>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleLogoSelected}
              />
              {logoError && (
                <Typography variant="caption" color="error">
                  {logoError}
                </Typography>
              )}
            </div>
          )}
          <TextField
            className={classes.formControl}
            margin="dense"
            id="name"
            label="Name"
            type="text"
            variant="filled"
            value={state.name}
            onChange={handleChange}
            error={errors.name}
            helperText={errors.name}
          />
          {isLoaded && (
            <Autocomplete
              onLoad={handleLoad}
              onPlaceChanged={handlePlaceChanged}
              className={classes.formControl}
            >
              <TextField
                autoFocus
                className={classes.formControl}
                margin="dense"
                id="street"
                label="Address"
                type="text"
                variant="filled"
                value={state.street}
                onChange={handleChange}
              />
            </Autocomplete>
          )}
          <TextField
            className={classes.formControl}
            margin="dense"
            id="aptUnit"
            label="Unit, suite, or floor #"
            type="text"
            variant="filled"
            value={state.aptUnit}
            onChange={handleChange}
          />
          <TextField
            className={classes.formControl}
            margin="dense"
            id="city"
            label="City"
            type="text"
            variant="filled"
            value={state.city}
            onChange={handleChange}
          />
          <Row spacing={2}>
            <TextField
              className={classes.formControl}
              margin="dense"
              id="stateProvince"
              label="State/Province"
              type="text"
              variant="filled"
              value={state.stateProvince}
              onChange={handleChange}
            />
            <TextField
              className={classes.formControl}
              margin="dense"
              id="postalCode"
              label="Postal code"
              type="text"
              variant="filled"
              value={state.postalCode}
              onChange={handleChange}
            />
          </Row>
          <TextField
            className={classes.formControl}
            margin="dense"
            id="countryRegion"
            label="Country/Region"
            type="text"
            variant="filled"
            value={state.countryRegion}
            onChange={handleChange}
          />
          {/* <FormControl variant="filled" className={classes.formControl}>
            <InputLabel id="timezone-label">Default Time Zone</InputLabel>
            <Select labelId="timezone-label" id="timezone">
              {timezoneOptions}
            </Select>
          </FormControl> */}
          <TextField
            className={classes.formControl}
            margin="dense"
            id="phone"
            label="Phone"
            type="text"
            variant="filled"
            value={state.phone}
            onChange={handleChange}
          />
          <TextField
            className={classes.formControl}
            margin="dense"
            id="url"
            label="Website/Url"
            type="text"
            variant="filled"
            value={state.url}
            onChange={handleChange}
          />
          <TextField
            className={classes.formControl}
            margin="dense"
            id="seats"
            label="Number of Seats"
            type="number"
            variant="filled"
            value={state.seats}
            onChange={handleChange}
            error={!!errors.seats}
            helperText={errors.seats}
            inputProps={{ min: 1 }}
          />
          {!organizationId && (
            <TextField
              className={classes.formControl}
              margin="dense"
              id="trialEndsAt"
              label="Free Trial Period"
              type="date"
              variant="filled"
              value={state.trialEndsAt}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          )}

          {organizationId && isSuperAdmin && (
            <div className={classes.trialSection}>
              <Typography variant="subtitle2" gutterBottom>
                Free Trial Period
              </Typography>
              <Row spacing={2} verticalAlignment="centerVertically">
                <TextField
                  className={classes.trialDateField}
                  margin="dense"
                  label="Ends On"
                  type="date"
                  variant="filled"
                  value={trialDate}
                  onChange={(event) => setTrialDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={savingTrial}
                />
                <ProgressButton
                  variant="outlined"
                  color="primary"
                  loading={savingTrial}
                  disabled={!trialDate}
                  onClick={handleApplyTrial}
                >
                  Apply Trial
                </ProgressButton>
                {organization?.trialEndsAt && (
                  <Button
                    className={classes.destructiveButton}
                    variant="outlined"
                    disabled={savingTrial}
                    onClick={handleClearTrial}
                  >
                    Clear Trial
                  </Button>
                )}
              </Row>
              <Typography variant="caption" color="textSecondary" display="block">
                {describeTrial(organization?.trialEndsAt)}. Staff are alerted the
                morning after a trial ends, so a past date sends an alert
                tomorrow.
              </Typography>
              {trialError && (
                <Typography variant="caption" color="error" display="block">
                  {trialError}
                </Typography>
              )}
            </div>
          )}
        </Column>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditOrganizationForm.propTypes = {
  organizationId: PropTypes.string,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
};

export default EditOrganizationForm;
